import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Home, Package, ExternalLink, Plus, Trash2, FileText, Pencil } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Modal } from '../../../components/ui/Modal';
import { Badge } from '../../../components/ui/Badge';
import { LoadingSpinner } from '../../../components/ui/LoadingSpinner';
import { useToast } from '../../../components/ui/Alert';
import { pagesApi } from '../../../api/endpoints/pages';
import { PAY_URL } from '../../../lib/constants';
import type { StorePage, CreatePageInput } from '../../../types/page';

interface Props {
  storeId: string;
}

const STOREFRONT_PAGES = [
  {
    id: 'home',
    icon: Home,
    titleKey: 'storefrontPages.home.title',
    titleFallback: 'الصفحة الرئيسية',
    descKey: 'storefrontPages.home.description',
    descFallback: 'الصفحة الأولى التي يراها زوار متجرك',
  },
  {
    id: 'product',
    icon: Package,
    titleKey: 'storefrontPages.product.title',
    titleFallback: 'صفحة المنتج',
    descKey: 'storefrontPages.product.description',
    descFallback: 'القالب المستخدم لعرض كل منتج في المتجر',
  },
] as const;

/** Slugify an Arabic / Latin title → URL-safe path segment. */
function toSlug(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
    .replace(/[^a-z0-9؀-ۿ-]+/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

export function PagesTab({ storeId }: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Editor lives at the pay server root (/dashboard/:id/editor/page/:slug),
  // NOT under /api/v1. VITE_API_BASE_URL includes the /api/v1 suffix, so use
  // PAY_URL (= the origin) to build the editor URL.
  const openInEditor = (pageSlug: string) => {
    window.open(`${PAY_URL}/dashboard/${storeId}/editor/page/${pageSlug}`, '_blank');
  };

  // ── Custom-pages CRUD state ──
  const [createOpen, setCreateOpen] = useState(false);
  const [newPage, setNewPage] = useState<CreatePageInput>({ title: '', slug: '', type: 'page' });
  const [deleteTarget, setDeleteTarget] = useState<StorePage | null>(null);
  const [slugTouched, setSlugTouched] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['pages', storeId],
    queryFn: () => pagesApi.list(storeId),
    enabled: !!storeId,
  });

  const customPages = useMemo(
    () => (data?.customPages ?? []).filter(p => (p.type ?? 'page') === 'page'),
    [data],
  );

  const createMutation = useMutation({
    mutationFn: (input: CreatePageInput) => pagesApi.create(storeId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pages', storeId] });
      toast('success', t('common.success'));
      setCreateOpen(false);
      setNewPage({ title: '', slug: '', type: 'page' });
      setSlugTouched(false);
    },
    onError: () => toast('error', t('common.error')),
  });

  const deleteMutation = useMutation({
    mutationFn: (pageId: string) => pagesApi.delete(storeId, pageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pages', storeId] });
      toast('success', t('common.success'));
      setDeleteTarget(null);
    },
    onError: () => toast('error', t('common.error')),
  });

  const handleCreate = () => {
    const title = newPage.title.trim();
    const slug = (newPage.slug || toSlug(title)).trim();
    if (!title || !slug) return;
    if (customPages.some(p => p.slug === slug)) {
      toast('error', t('storefrontPages.slugExists', 'هذا المعرف مستخدم — اختر آخر'));
      return;
    }
    createMutation.mutate({ title, slug, type: 'page' });
  };

  return (
    <div className="space-y-6">
      {/* ── Storefront templates (Home + Product) ── */}
      <section className="space-y-3">
        <div>
          <h2 className="font-semibold">{t('storefrontPages.templates', 'صفحات المتجر')}</h2>
          <p className="text-sm text-text-muted">
            {t('storefrontPages.subtitle', 'افتح أي صفحة في محرر التصميم لتعديل المحتوى والتنسيق.')}
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {STOREFRONT_PAGES.map(page => (
            <div
              key={page.id}
              className="flex items-start gap-3 rounded-xl border border-border-default bg-bg-secondary p-4"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent">
                <page.icon className="h-5 w-5" />
              </div>
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                <div>
                  <h3 className="font-semibold">{t(page.titleKey, page.titleFallback)}</h3>
                  <p className="text-xs text-text-muted">{t(page.descKey, page.descFallback)}</p>
                </div>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => openInEditor(page.id)}
                  className="self-start"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  {t('storefrontPages.openInEditor', 'فتح في المحرر')}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Custom pages ── */}
      <section className="space-y-3">
        <div className="flex items-end justify-between gap-3">
          <div>
            <h2 className="font-semibold">{t('storefrontPages.custom', 'صفحات مخصصة')}</h2>
            <p className="text-sm text-text-muted">
              {t(
                'storefrontPages.customSubtitle',
                'أنشئ صفحات إضافية مثل "من نحن" أو "سياسة الخصوصية" وحرّرها بالمحرر.',
              )}
            </p>
          </div>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" />
            {t('storefrontPages.create', 'إنشاء صفحة')}
          </Button>
        </div>

        {isLoading ? (
          <div className="py-8">
            <LoadingSpinner />
          </div>
        ) : customPages.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border-default bg-bg-secondary p-8 text-center">
            <FileText className="mx-auto h-8 w-8 text-text-muted" />
            <p className="mt-3 text-sm text-text-muted">
              {t('storefrontPages.empty', 'لا توجد صفحات مخصصة بعد.')}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {customPages.map(page => (
              <div
                key={page._id}
                className="flex items-center gap-3 rounded-xl border border-border-default bg-bg-secondary p-3"
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-bg-tertiary text-text-muted">
                  <FileText className="h-4 w-4" />
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="truncate font-medium">{page.title}</span>
                  <div className="flex items-center gap-2 text-xs text-text-muted">
                    <code dir="ltr" className="rounded bg-bg-tertiary px-1.5 py-0.5">/{page.slug}</code>
                    {(page.published === false || page.isPublished === false) && (
                      <Badge variant="warning">
                        {t('storefrontPages.draft', 'مسودة')}
                      </Badge>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => openInEditor(page.slug)}
                  title={t('storefrontPages.openInEditor', 'فتح في المحرر')}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setDeleteTarget(page)}
                  title={t('common.delete', 'حذف')}
                  className="text-danger"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── Create modal ── */}
      <Modal
        isOpen={createOpen}
        onClose={() => { setCreateOpen(false); setSlugTouched(false); }}
        title={t('storefrontPages.create', 'إنشاء صفحة')}
        footer={
          <>
            <Button variant="secondary" onClick={() => { setCreateOpen(false); setSlugTouched(false); }}>
              {t('common.cancel', 'إلغاء')}
            </Button>
            <Button onClick={handleCreate} isLoading={createMutation.isPending}>
              {t('common.create', 'إنشاء')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <div>
            <label className="mb-1 block text-sm font-medium">
              {t('storefrontPages.title', 'العنوان')}
            </label>
            <Input
              value={newPage.title}
              autoFocus
              onChange={e => {
                const title = e.target.value;
                setNewPage(p => ({
                  ...p,
                  title,
                  slug: slugTouched ? p.slug : toSlug(title),
                }));
              }}
              placeholder={t('storefrontPages.titlePlaceholder', 'مثال: من نحن') as string}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">
              {t('storefrontPages.slug', 'المعرف (slug)')}
            </label>
            <Input
              value={newPage.slug}
              dir="ltr"
              onChange={e => {
                setSlugTouched(true);
                setNewPage(p => ({ ...p, slug: toSlug(e.target.value) }));
              }}
              placeholder="about-us"
            />
            <p className="mt-1 text-xs text-text-muted">
              {t('storefrontPages.slugHelp', 'يظهر في رابط الصفحة، مثلاً: /about-us')}
            </p>
          </div>
        </div>
      </Modal>

      {/* ── Delete confirmation ── */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={t('common.confirm', 'تأكيد')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
              {t('common.cancel', 'إلغاء')}
            </Button>
            <Button
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget._id)}
              isLoading={deleteMutation.isPending}
              className="bg-danger text-white hover:bg-danger/90"
            >
              {t('common.delete', 'حذف')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-text-secondary">
          {t(
            'storefrontPages.deleteConfirm',
            'هل أنت متأكد من حذف هذه الصفحة؟ لا يمكن التراجع.',
          )}{' '}
          <strong>{deleteTarget?.title}</strong>
        </p>
      </Modal>
    </div>
  );
}
